import { get, postJson, patch, deleteRequest, handleResponse, API_BASE_URL } from './apiClient';
import { getAuthHeader } from './authSession';
import type { RfqHeaderFacts } from '../utils/rfqHeaderFactRows';

export type QuotationStatus = 'needs_review' | 'finalized' | 'sent' | 'rejected';
/** D161.3: what the list filters by: a stored status, or Confirmed (the RFQ has an order; never stored). */
export type QuotationListStatus = QuotationStatus | 'confirmed';

export interface QuotationListItemDto {
  _id: string;
  quotationNumber: string;
  // Two live shapes: a raw id, or the populated RFQ. `customerName` and
  // `vesselName` are declared because the list endpoint has always selected
  // them and the table now READS them — they are what fills the Client Name and
  // Vessel Name columns for a quotation whose own snapshot predates the value.
  // The detail read also carries the RFQ's header facts (the buyer's own RFQ number, port, dates,
  // currency): `RfqHeaderFacts`, shown beside the vessel in the header.
  rfqId: string | ({ _id: string; rfqId?: string; subject?: string; customerName?: string; vesselName?: string } & RfqHeaderFacts);
  rfqNumber?: string;
  /** Email subject from linked RFQ (set by GET /api/quotations list) */
  rfqSubject?: string | null;
  customer: {
    customerName?: string;
    vesselName?: string;
  };
  status: QuotationStatus;
  summary: {
    totalItems: number;
    itemsWithPricing: number;
    itemsWithoutPricing: number;
    totalValue: number;
    currency: string;
  };
  createdAt: string;
  generatedAt: string;
  /**
   * D81 / D161.3 — the confirmed order of this quotation's RFQ (any version), read with the list
   * and the detail alike; null when there is none. Its quantities change on the order, not here.
   */
  confirmedOrder?: QuotationConfirmedOrderDto | null;
}

export interface VendorOptionDto {
  vendorName: string;
  unitPrice: number;
  totalPrice: number;
  currency: string;
  pricingSource: 'historical_quotation' | 'yelo_api' | 'price_list' | 'client_provided' | 'master_catalogue';
  pricingConfidence: number;
  pricingDetails?: any;
  internalRemarks?: string;
}

export interface QuotationItemDto {
  // D99 item 4 (TECH_DEBT #344): the line's identity, minted by the server. Send back what the
  // server sent; a line added in the browser carries none (the server keys it on save). A key the
  // server does not hold is ignored, so a copied line is a new line.
  lineKey?: string;
  lineNumber: number;
  rfqItemId?: string | null;
  itemName: string;
  itemDescription?: string;
  quantity: number;
  // TECH_DEBT #65 — set only when the RFQ line's own quantity was a stated
  // literal 0 (not missing — invariant 8). The line still prices normally
  // (0 x unitPrice = $0); this only flags it for a reviewer, the same shape
  // `unitMismatch` below uses for a different question. No hard block on
  // finalize/send. Absent/false on every line generated before 2026-08-31.
  quantityFlag?: boolean;
  uom?: string;
  // Set only when the pricing engine could not find a real ratio between the
  // unit the RFQ asked for and the unit it actually has a price in
  // (TECH_DEBT #61 — e.g. CASE was asked for, only a PCS price is known, no
  // Units/Case stated on the item). `uom` above is then the HONEST quoted
  // unit, not the requested one; `unitRequested` is what the vessel asked for.
  // Fix by adding Units/Case (and Unit Size, for a volume charge) to the item
  // in Master Catalogue, then re-fetching this line's price.
  unitMismatch?: boolean;
  unitRequested?: string | null;
  /** D105.10: WHY `unitMismatch` is set (server enum, `Quotation.js`); null/absent on a legacy line. */
  unitMismatchReason?: string | null;
  /**
   * What the VESSEL asked for on this line, pinned at generation and never
   * rewritten (TECH_DEBT #151). Null on lines generated before 2026-08-27.
   *
   * Distinct from everything beside it: `quantity`/`uom` are what the line is
   * QUOTED as and a restatement moves both; `unitRequested` is null whenever the
   * units agree and carries no amount; `pricingDetails.conversionAnchor` anchors
   * the LAST established price/quantity for restatement math and is replaced by
   * any hand edit, on purpose. Read it, never write it — the server carries it
   * across updates (`preserveItemProvenance`) and would restore it anyway.
   */
  originalRequest?: { quantity?: number | null; uom?: string | null } | null;
  unitPrice?: number;
  /**
   * The TT$ this line's COST was stated at, pinned when it was priced — the
   * Master Catalogue / vendor-invoice / supplier figure itself, not a
   * conversion of `unitPrice`. Null means the cost was stated in US$ and the
   * TT$ columns derive. Read through `utils/pricing.unitCostTtd`, never
   * rendered raw.
   */
  unitPriceTtd?: number | null;
  /**
   * D85: the UNROUNDED US$ cost `unitPrice` was ceiled from. The markup is applied to
   * it, rounded up once (`utils/pricing.markupCostUsd`, the only reader). Null/absent
   * means none is known; the server drops it whenever it stops explaining `unitPrice`.
   */
  unitPriceExact?: number | null;
  currency: string;
  totalPrice?: number;
  markup?: number | null; // Markup percentage for this item (null: a locked line with no cost)
  unitSellingPrice?: number; // Unit selling price (can be manually overridden)
  totalSellingPrice?: number; // Total selling price (can be manually overridden)
  competitorPrice?: number; // Competitor's price for comparison
  /** D97: the selling price this line must sell at (an IFS list price). Cleared when a person types a selling price or markup. */
  statedSellingPrice?: number | null;
  /** D97: the unit `statedSellingPrice` is stated per; set with it, cleared with it, never rewritten (a locked line never changes unit). */
  statedSellingUom?: string | null;
  vat?: boolean; // Whether this line's cost is grossed for VAT, at the Settings rate (getVatRate) — never a hardcoded rate
  /**
   * The Master Catalogue has no row that could price this item.
   *
   * INDEPENDENT of `pricingSource`: a line a historical quotation or a price
   * list ended up pricing is still a line the catalogue has never heard of, and
   * that is exactly the line worth adding. False also means "we could not find
   * out" — the server leaves it unset when the catalogue search itself errored.
   *
   * Written by `getItemPricing` Step 1 and stored on the quotation item; the
   * grid highlights on it.
   */
  notInCatalogue?: boolean;
  /** D99: catalogue-step match label (`legacy` = stored before D99) + reason codes. */
  matchDecision?: string;
  matchReasons?: string[];
  matchAcknowledgedBy?: string | null;
  /** Response-only: the confirmer's display name, stamped by the server on read; never sent back. */
  matchAcknowledgedByName?: string | null;
  matchAcknowledgedAt?: string | null;
  /** D99.16: who confirmed this wording earlier (server-owned), their name (response-only) and when. */
  matchRememberedBy?: string | null;
  matchRememberedByName?: string | null;
  matchRememberedAt?: string | null;
  /**
   * Catalogue rows that came back for this item but failed the pricing gate.
   * Non-zero means adding a new row risks DUPLICATING one already there, so the
   * add dialog warns rather than quietly worsening the rows-per-product ratio.
   */
  catalogueNearMisses?: number;
  /**
   * A Master Catalogue row this line seeded was thrown away by a person, and
   * must not come back.
   *
   * DECLARED SO IT IS ROUND-TRIPPED, which is the whole reason it appears in a
   * type nothing here reads. The grid PATCHes the WHOLE items array on every
   * cell edit, so a field the screen does not send back is deleted by the
   * server's assignment — and this one survived only because every edit path
   * happens to spread `{...item}` over a property TypeScript had never heard
   * of. One field-by-field rebuild in this file would have silently un-removed
   * every row anybody had ever removed.
   *
   * Read it, never write it. Removals made from 2026-09-07 are recorded on the
   * quotation instead (server-owned, and never sent by the screen); this field
   * carries the ones made before that date. TECH_DEBT #274.
   */
  catalogueRowRemoved?: boolean;
  pricingSource: 'historical_quotation' | 'yelo_api' | 'price_list' | 'vendor_invoice' | 'client_provided' | 'not_found' | 'master_catalogue' | 'manual_entry';
  pricingConfidence: number;
  selectedVendor?: string;
  vendorOptions?: VendorOptionDto[];
  notes?: string;
  internalRemarks?: string;
  remarks?: string;
  /** The packaging sentence the system last put at the front of `remarks`, exactly (owner decision 2026-09-26). */
  packagingRemark?: string | null;
  moreDetails?: string;
  /** Basis / unit conversion info from pricing engine (atomic count, weight conversion, etc.) */
  pricingDetails?: Record<string, unknown>;
}

export interface CustomExpenseDto {
  _id?: string;
  label: string;
  amount: number;
  currency?: string;
  notes?: string;
}

/**
 * The charges built INTO the line selling prices, in US$ (client meeting
 * 2026-09-25). Server-owned (D170.5: the charge spread writes it); additive;
 * internal only. See the schema comment in src/models/Quotation.js.
 */
export interface AbsorbedChargesDto {
  transportation: number;
  additional: number;
  customExpenses: Array<{ label: string; amount: number }>;
  total: number;
  absorbedAt: string | null;
}

export interface QuotationDetailDto extends QuotationListItemDto {
  items: QuotationItemDto[];
  terms?: {
    paymentTerms?: string;
    deliveryTerms?: string;
    warranty?: string;
    additionalNotes?: string;
  };
  discountPercentage?: number;
  transportationCharges?: number;
  additionalCharges?: number;
  customExpenses?: CustomExpenseDto[];
  absorbedCharges?: AbsorbedChargesDto;
  /** D170.5: the last charge spread, present only while it can still be undone (server-owned). */
  chargeSpread?: { at: string } | null;
  competitorDiscountPercentage?: number;
  competitorTransportationCharges?: number;
  competitorAdditionalCharges?: number;
  competitorCustomExpenses?: CustomExpenseDto[];
  validUntil?: string;
  reviewedAt?: string;
  reviewedBy?: string;
  reviewNotes?: string;
  /**
   * USD ↔ TTD rate this quotation is priced at, locked when it was generated.
   * Changing the rate in Settings does not move it — use this rather than the
   * live setting whenever displaying TTD for this quotation.
   */
  exchangeRate?: {
    ttdToUsd?: number | null; // 1 TTD = x USD (e.g. 0.1667)
    usdToTtd?: number | null; // 1 USD = x TTD (e.g. 6)
    source?: string;
    lockedAt?: string | null;
  };
  /**
   * D45.2 — versions. A sent quotation is never edited; a client's change goes into a
   * version numbered `<revisionRootNumber>-<revisionNumber>`. Null / 0 on an original.
   */
  revisionOf?: string | null;
  revisionNumber?: number;
  revisionRootNumber?: string | null;
  /** D45.1 — the client changes still waiting for a person, decided by the server. */
  pendingClientAmendments?: ClientAmendmentDto[];
}

export interface QuotationConfirmedOrderDto {
  _id: string;
  /** The version the order was confirmed from — how an order is named on screen. */
  quotationNumber?: string;
}

/** One line a client added by email, as it went onto the quotation (D45.1). */
export interface ClientAmendmentAddedItemDto {
  itemName: string;
  /** As the client asked. Null when the email gave no quantity (missing is not zero). */
  quantity: number | null;
  uom: string | null;
  lineNumber: number | null;
  /** False when no pricing source knew the item — the line is on the quotation with no cost. */
  priced: boolean;
}

/** One quantity/unit change a client asked for on an existing line (D45.1). */
export interface ClientAmendmentCorrectionDto {
  lineIndex: number;
  lineNumber: number | null;
  itemName: string;
  oldQuantity: number | null;
  newQuantity: number | null;
  oldUom: string | null;
  newUom: string | null;
  /** False for a change of unit: recorded, not applied — the price is per the line's own unit. */
  applied: boolean;
  reason: string | null;
}

/** A change a client asked for by email that nobody has reviewed yet (D45.1). */
export interface ClientAmendmentDto {
  _id: string;
  quotationId: string;
  quotationNumber: string;
  mode: 'amended_in_place' | 'new_version';
  sourceEmailId: string | null;
  sentQuotationId: string | null;
  receivedAt: string | null;
  appliedAt: string | null;
  addedItems: ClientAmendmentAddedItemDto[];
  correctedItems: ClientAmendmentCorrectionDto[];
}

/** One member of a quotation's version chain, with the line fields a comparison reads (D45.2). */
export interface QuotationVersionDto {
  _id: string;
  quotationNumber: string;
  revisionOf: string | null;
  revisionNumber: number;
  status: QuotationStatus;
  createdAt: string | null;
  sentAt: string | null;
  items: Array<
    Pick<
      QuotationItemDto,
      'lineNumber' | 'itemName' | 'quantity' | 'uom' | 'currency' | 'unitPrice' | 'totalPrice' | 'markup' | 'unitSellingPrice' | 'totalSellingPrice'
    >
  >;
}

export interface QuotationVersionChainDto {
  quotationId: string;
  rootId: string;
  /** Oldest first: the original, then version 1, 2, … */
  versions: QuotationVersionDto[];
}

export interface QuotationListResponse {
  success: boolean;
  data: {
    quotations: QuotationListItemDto[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

export interface GenerateQuotationResponse {
  success: boolean;
  message: string;
  data?: any;
}

// Fetch all quotations with filters and pagination
export async function fetchQuotationList(params: {
  page?: number;
  limit?: number;
  /** Single status or comma-separated list of statuses. */
  status?: QuotationStatus | string;
  customer?: string;
  /** Vessel name, partial match — the quotation's own copy or the RFQ's. */
  vessel?: string;
  rfqNumber?: string;
  quotationNumber?: string;
  /** Free-text search across email subject, client name, vessel, RFQ & quote number. */
  search?: string;
  /** Inclusive createdAt range (YYYY-MM-DD). */
  dateFrom?: string;
  dateTo?: string;
  /** Only quotations that can still be confirmed: no confirmed order on the quotation or its RFQ. */
  unconfirmed?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
} = {}) {
  const searchParams = new URLSearchParams();

  if (params.page) searchParams.set('page', String(params.page));
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.status) searchParams.set('status', params.status);
  if (params.customer) searchParams.set('customer', params.customer);
  if (params.vessel) searchParams.set('vessel', params.vessel);
  if (params.rfqNumber) searchParams.set('rfqNumber', params.rfqNumber);
  if (params.quotationNumber) searchParams.set('quotationNumber', params.quotationNumber);
  if (params.search) searchParams.set('search', params.search);
  if (params.dateFrom) searchParams.set('dateFrom', params.dateFrom);
  if (params.dateTo) searchParams.set('dateTo', params.dateTo);
  if (params.unconfirmed) searchParams.set('unconfirmed', '1');
  if (params.sortBy) searchParams.set('sortBy', params.sortBy);
  if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);

  return get<QuotationListResponse>(`/quotations?${searchParams.toString()}`);
}

// Fetch quotation by ID
export async function fetchQuotationDetail(id: string) {
  const response = await get<{ success: boolean; data: QuotationDetailDto }>(`/quotations/${id}`);
  return response.data;
}

/** Every version of the quotation `id` belongs to, oldest first (D45.2). */
export async function fetchQuotationVersions(id: string) {
  const response = await get<{ success: boolean; data: QuotationVersionChainDto }>(`/quotations/${id}/revisions`);
  return response.data;
}

/**
 * Mark one client change reviewed (D45.1). Returns what is still waiting on that quotation.
 * Refusals carry `code`: QUOTATION_NOT_FOUND, QUOTATION_AMENDMENT_NOT_FOUND.
 */
export async function markClientAmendmentReviewed(quotationId: string, amendmentId: string) {
  const response = await postJson<{
    success: boolean;
    data: { quotationId: string; pendingClientAmendments: ClientAmendmentDto[] };
  }>(`/quotations/${quotationId}/amendments/${amendmentId}/reviewed`, {});
  return response.data;
}

// Generate quotations for all processed RFQs
export async function generateQuotations() {
  return postJson<GenerateQuotationResponse>('/quotations/generate', {});
}

// Generate quotation for specific RFQ
export async function generateQuotationForRFQ(rfqId: string) {
  return postJson<GenerateQuotationResponse>(`/quotations/generate/${rfqId}`, {});
}

/**
 * D99.13: retry a failed generation (RFQ in 'error'). Resolves with the new quotation's id.
 * Refusals carry `code`: RFQ_NOT_RETRYABLE, RFQ_NOT_FOUND, or the generation's own (VAT_RATE_NOT_SET...).
 */
export async function retryQuotationGeneration(rfqId: string) {
  const response = await postJson<{ success: boolean; data: { quotationId: string } }>(
    `/quotations/generate/${rfqId}/retry`,
    {}
  );
  return response.data.quotationId;
}

/**
 * What the server did with this quotation's unpriced lines when the status
 * change froze it.
 *
 * Rides on every response that can freeze a quotation, and is a no-op report
 * (`ran: false`) on every status that is not `finalized` / `sent` / `rejected`.
 * The screen states the outcome rather than leaving an automatic write to the
 * Master Catalogue invisible — see `quotationCatalogueHarvest.service.js`.
 */
export type CatalogueHarvestResult = {
  /** False when the status is not one of the frozen three — nothing was looked at. */
  ran: boolean;
  status: string;
  /** Rows written to the Master Catalogue by this call. */
  created: number;
  /**
   * Lines that already had their row from an earlier freeze — whether the row
   * was then left alone, repriced, or held for review.
   */
  alreadyPresent: number;
  /**
   * Rows whose price followed this quotation's cost. Only ever non-zero while
   * the quotation is `sent` — a finalized or rejected one leaves its rows alone.
   */
  repriced: number;
  /** Price changes too large to apply unasked; parked on the row instead. */
  repriceAsked: number;
  proposals: Array<{
    lineIndex: number;
    itemName: string;
    fromPrice: number | null;
    toPrice: number;
    changePct: number | null;
    reason: string;
  }>;
  skipped: Array<{ lineIndex: number; itemName: string; reason: string }>;
  failures: Array<{ lineIndex: number; itemName: string; message: string; code?: string }>;
};

/** A price change waiting on a harvested row for somebody to decide. */
export type PendingReprice = {
  /**
   * NULL when the row had no comparable old price — a row in another currency,
   * or one holding no price at all. Check before formatting.
   */
  fromPrice: number | null;
  toPrice: number;
  currency: string;
  /**
   * Signed, relative to `fromPrice`: +900 is a tenfold rise, -90 a tenfold fall.
   * NULL when the change is unmeasurable, i.e. whenever `fromPrice` is.
   */
  changePct: number | null;
  /** `size` (over the threshold), `currency` (row is in another currency), `no_baseline`. */
  reason: string;
  proposedAt?: string;
};

/** One Master Catalogue row a quotation seeded when it froze. */
export type HarvestedCatalogueRow = {
  _id: string;
  item_name: string;
  unit: string | null;
  item_price: number | null;
  currency: string | null;
  source: string;
  /** Which line of THIS quotation produced it — the grid row it belongs to. */
  origin?: { lineIndex?: number | null };
  last_updated?: string;
  /** Present when a re-freeze wanted to move this row's price and did not dare. */
  pendingReprice?: PendingReprice | null;
};

/**
 * What freezing this quotation left in the Master Catalogue.
 *
 * Asked of the catalogue rows themselves, so it survives a page reload and
 * cannot disagree with what is really stored — a row deleted from the Master
 * Catalogue page simply stops being listed here.
 */
export async function fetchQuotationCatalogueRows(id: string) {
  return get<{ success: boolean; data: { rows: HarvestedCatalogueRow[] } }>(
    `/quotations/${id}/catalogue-rows`
  );
}

/**
 * How a quotation line's CURRENT cost was reached, as the server reads it off
 * the line (`quotationLineCatalogueOptions.pricedFromFacts`).
 */
export type LinePricedHow =
  | 'catalogue_row'
  | 'catalogue_search_pick'
  | 'supplier_pick'
  | 'not_priced'
  | 'other_source';

/**
 * One supplier's price as pricing recorded it when it chose between suppliers
 * (BUSINESS_RULES#catalogue-vendor-row-choice, D25).
 */
export interface LineSupplierPrice {
  supplier: string | null;
  /** Already written for a person by the server's money owner, e.g. "TTD 40.00". */
  priceText: string | null;
  /** An invoice's own calendar day ("2026-09-10") or an ISO instant; null when the price has no date. */
  date: string | null;
  /** What the date is: 'invoice', 'price_list', 'price_edit' or 'added'; null with no date. */
  dateBasis: string | null;
  /** The product, sent only when a keyword group took part (BUSINESS_RULES#catalogue-keyword-group). */
  item?: string;
}

/**
 * The Master Catalogue rows one quotation line could be priced from, and the one
 * it WAS priced from (BUSINESS_RULES#quotation-line-catalogue-options).
 */
export interface QuotationLineCatalogueOptions {
  quotation: { _id: string; quotationNumber: string | null };
  /** The line as it stands NOW — a line number is a position and can move. */
  line: { lineNumber: number; itemName: string; quantity: number | null; uom: string | null };
  pricedFrom: {
    how: LinePricedHow;
    masterCatalogueId: string | null;
    pricingSource: string | null;
    selectedVendor: string | null;
    /** What the pricing step recorded at the time; null when it recorded nothing. */
    units: { referenceUnit: string | null; pricedPer: string | null; basis: string | null } | null;
    /**
     * What pricing recorded about choosing the row — on a 'catalogue_row' line
     * only, and null when nothing was recorded: whether the model accepted it as a
     * substitute, and the reasoning given. Optional, so a client ahead of its
     * server reads "nothing recorded".
     */
    choice?: {
      substitute: boolean | null;
      reasoning: string | null;
      /**
       * Which supplier's price quoted the line, and every other supplier's latest
       * (BUSINESS_RULES#catalogue-vendor-row-choice, D25); null when no choice was
       * recorded. Optional, so a client ahead of its server reads "nothing recorded".
       */
      supplierChoice?: {
        /** 'most_recent' or 'no_dated_price'; a line priced under D23 carries its old rule name. */
        rule: string | null;
        /** A client's preferred vendor quoted the line instead of `chosen`. */
        preferredDisplaced: boolean;
        chosen: LineSupplierPrice;
        alternatives: LineSupplierPrice[];
        /**
         * A keyword group took part (BUSINESS_RULES#catalogue-keyword-group): which one, and
         * whether it priced the line (the line named no brand) or only listed its other
         * products beside a line that named one. Absent when no group took part.
         */
        group?: { keyword: string | null; pricedByGroup: boolean };
        /** The group's other products, each supplier's latest. Absent when no group took part. */
        otherProducts?: LineSupplierPrice[];
      } | null;
    } | null;
  };
  usedRow: {
    /** 'none' — the line records no row; 'merged' — `rowId` is the row it was merged into. */
    status: 'none' | 'found' | 'merged' | 'missing';
    requestedId: string | null;
    rowId: string | null;
    /** Whether the priced row is the same product as the line's name today. False keeps it pinned, and is worth saying. */
    matchesItem: boolean;
  };
  /** The priced row first (when there is one), then the other rows of the same product, closest name first. */
  rowIds: string[];
}

export async function fetchQuotationLineCatalogueOptions(quotationId: string, lineNumber: string | number) {
  return get<{ success: boolean; data: QuotationLineCatalogueOptions }>(
    `/quotations/${encodeURIComponent(quotationId)}/items/${encodeURIComponent(String(lineNumber))}/catalogue-options`
  );
}

/**
 * D99 item 4: a person vouches for a flagged catalogue match. The server stores who and
 * when. Refusals carry `code`: MATCH_NOT_CONFIRMABLE, MATCH_LINE_CHANGED,
 * QUOTATION_NOT_FOUND, QUOTATION_LINE_NOT_FOUND.
 */
export async function confirmLineMatch(quotationId: string, lineNumber: string | number) {
  const response = await patch<{ success: boolean; data: { line: QuotationItemDto } }>(
    `/quotations/${encodeURIComponent(quotationId)}/items/${encodeURIComponent(String(lineNumber))}/match-confirmation`,
    {}
  );
  return response.data.line;
}

/**
 * The ONE Master Catalogue row backing one quotation line — the Supplier
 * cell's read-only details card (N7, owner decision, 2026-09-25). Narrowed,
 * server-side, to exactly these fields — never the full `MasterCatalogueItem`
 * shape `masterCatalogueApi.getMasterCatalogueItem` returns, which this card
 * used to call and which is gated on PRODUCTS view: this endpoint is gated on
 * QUOTATIONS view alone, because the id it reads comes off the LINE's own
 * `pricingDetails.masterCatalogueId`, resolved server-side, never accepted
 * from the caller.
 */
export interface QuotationLineCatalogueRowDto {
  _id: string;
  item_name: string | null;
  unit: string | null;
  units_per_case: number | null;
  unit_size: string | null;
  unitConversions: unknown;
  uomMeta: Record<string, unknown> | null;
  /** The same fallback text `masterCatalogueApi`'s full read already carries. */
  packagingGap: string | null;
  supplier: string | null;
  source: string | null;
  priceLevels: { byUnit: Array<{ unit: string; ttAmount: number }> } | null;
  /** D86: ISO date this row's price counts from (server `priceDateOf`), or null when nothing dates it. */
  priceDate?: string | null;
  /** What dated that price: 'invoice' | 'price_list' | 'added' | 'price_edit', or null. Read by `priceSourceLabel`. */
  priceDateBasis?: string | null;
}

/**
 * Fetch it. `itemIndex` is the line's POSITION in the quotation's `items`
 * array — the same 0-based index its `vendor` / `vendor/preview` siblings
 * already address a line by.
 *
 * Refusals carry `code`: `QUOTATION_NOT_FOUND`, `QUOTATION_LINE_INVALID`,
 * `QUOTATION_LINE_NO_CATALOGUE_ROW` (this line carries no catalogue row, or
 * the one it carried is gone) — the caller reads `err.status`/`err.code`
 * (invariant 6), never `err.message`.
 */
export async function fetchQuotationLineCatalogueRow(quotationId: string, itemIndex: number) {
  return get<{ success: boolean; data: QuotationLineCatalogueRowDto }>(
    `/quotations/${encodeURIComponent(quotationId)}/items/${encodeURIComponent(String(itemIndex))}/catalogue-row`
  );
}

/**
 * Remove one harvested row and stop the next freeze putting it back.
 *
 * The server marks the originating line so re-freezing the quotation does not
 * re-add it — without that, "Remove" would last only until the next save.
 */
export async function removeQuotationCatalogueRow(id: string, rowId: string) {
  return deleteRequest<{ success: boolean; message: string; data: { removed: { item_name: string } } }>(
    `/quotations/${id}/catalogue-rows/${rowId}`
  );
}

/**
 * Answer a price change the re-freeze held back.
 *
 * `apply` moves the catalogue row to the quotation's current cost. `keep` leaves
 * the row alone AND records the price declined, so the next save of this
 * quotation does not propose the same figure again — without that, "Keep old"
 * would last only until the next edit.
 */
export async function resolveQuotationCatalogueReprice(
  id: string,
  rowId: string,
  decision: 'apply' | 'keep'
) {
  return patch<{
    success: boolean;
    message: string;
    data: { resolved: { item_name: string; decision: string; item_price: number } };
  }>(`/quotations/${id}/catalogue-rows/${rowId}/reprice`, { decision });
}

/**
 * Update a quotation's status.
 *
 * The ONE status call this client makes. There was a second — `updateQuotationStatus`,
 * against `PATCH /quotations/:id/status` — whose only caller was the quotation
 * detail page's `handleStatusUpdate`, which was never wired to a control. Both
 * are gone (TECH_DEBT #260); the server route stays, and now shares its rules
 * with this one instead of holding a divergent copy of them.
 *
 * Refusals arrive as a thrown error carrying `code`. `QUOTATION_HAS_UNPRICED_LINES`
 * means the quotation still has lines with no cost and so cannot be called
 * Finalized or Sent — its `message` names them.
 */
export async function updateStatus(id: string, status: QuotationStatus) {
  console.log('[API] Calling PATCH /quotations/' + id + ' with status:', status);
  const response = await patch<{
    success: boolean;
    data: QuotationDetailDto;
    catalogueHarvest?: CatalogueHarvestResult;
  }>(`/quotations/${id}`, {
    status
  });
  console.log('[API] PATCH response:', response);
  return response;
}

// Update quotation items and details
export async function updateQuotation(id: string, updates: Partial<QuotationDetailDto>) {
  return patch<{ success: boolean; data: QuotationDetailDto }>(`/quotations/${id}`, updates);
}

// Delete a quotation, e.g. to clean up one left behind by a deleted RFQ (QA Ticket 1).
export async function deleteQuotation(id: string) {
  return deleteRequest<{ success: boolean; message?: string }>(`/quotations/${id}`);
}

// CR-5 — Server-side global mark-up override. Preferred over the client-side
// item-map because the server does the calc + save atomically in one call.
export async function applyGlobalMarkup(id: string, markup: number) {
  return patch<{ success: boolean; data: QuotationDetailDto }>(`/quotations/${id}/global-markup`, { markup });
}

/**
 * D170.5: build the charges into the line selling prices (Final Price unchanged), and undo it.
 * Each answers the saved quotation. Refusals carry `code`: QUOTATION_FROZEN, QUOTATION_ORDERED,
 * QUOTATION_SPREAD_NOTHING, QUOTATION_SPREAD_NO_LINES, QUOTATION_SPREAD_UNDO_STALE, QUOTATION_NOT_FOUND.
 */
export async function spreadQuotationCharges(id: string) {
  return (await postJson<{ success: boolean; data: QuotationDetailDto }>(`/quotations/${encodeURIComponent(id)}/charge-spread`, {})).data;
}
export async function undoQuotationChargeSpread(id: string) {
  return (await postJson<{ success: boolean; data: QuotationDetailDto }>(`/quotations/${encodeURIComponent(id)}/charge-spread/undo`, {})).data;
}

export interface PriceLookupResult {
  itemName: string;
  quantity: number;
  uom: string;
  unitMismatch?: boolean;
  unitMismatchReason?: string | null;
  /** See `QuotationItemDto.notInCatalogue`. */
  notInCatalogue?: boolean;
  /** D99: catalogue-step match label (`legacy` = stored before D99) + reason codes. */
  matchDecision?: string;
  matchReasons?: string[];
  matchAcknowledgedBy?: string | null;
  /** Response-only: the confirmer's display name, stamped by the server on read; never sent back. */
  matchAcknowledgedByName?: string | null;
  matchAcknowledgedAt?: string | null;
  /** D99.16: who confirmed this wording earlier (server-owned), their name (response-only) and when. */
  matchRememberedBy?: string | null;
  matchRememberedByName?: string | null;
  matchRememberedAt?: string | null;
  catalogueNearMisses?: number;
  unitRequested?: string | null;
  /** D69: unpriced because the pack in the name contradicts the pack count on file. */
  packTextConflict?: boolean;
  unitPrice: number | null;
  /** The TT$ the source stated, when it stated one — pinned onto the line as `unitPriceTtd`. */
  unitPriceTtd?: number | null;
  /** D85: the unrounded US$ price `unitPrice` was ceiled from (see `QuotationItemDto.unitPriceExact`). */
  unitPriceExact?: number | null;
  totalPrice: number | null;
  currency: string;
  pricingSource: QuotationItemDto['pricingSource'];
  pricingConfidence: number;
  pricingDetails?: Record<string, unknown>;
  selectedVendor?: string | null;
  vendorOptions: VendorOptionDto[];
  internalRemarks?: string | null;
}

// Look up a price for an ad-hoc item using the same pipeline used when generating quotations
// (historical quotations → Yelo API → price lists → vendor invoices).
export async function lookupItemPrice(payload: {
  itemName: string;
  quantity?: number;
  uom?: string;
  /**
   * The line's current internal remark, from the per-line Refresh (D119.5): the answer's
   * `internalRemarks` is then that remark composed by the server - the replaced price's notes
   * out, the new ones in, `[rfq-match]`, `[buyer]` and typed text kept. Absent (the Add Item
   * popup): the lookup's own text.
   */
  internalRemarks?: string | null;
  /**
   * D157.1: the row a person matched the line to (`lineMatchReview.pinnedCatalogueRowId`), sent by the
   * per-line Refresh only: the server prices THAT row, or refuses CATALOGUE_ROW_GONE / CATALOGUE_ROW_UNPRICED.
   */
  masterCatalogueId?: string;
}) {
  const res = await postJson<{ success: boolean; data: PriceLookupResult }>(
    `/quotations/price-lookup`,
    payload
  );
  return res.data;
}

/**
 * The sentence for a failed price lookup, worded from the error's machine-readable `code` (CLAUDE.md
 * invariant 6) - never the server's prose. A code it does not know gets the caller's own generic sentence.
 */
export function lookupItemPriceErrorMessage(err: unknown, genericSentence: string): string {
  switch ((err as { code?: unknown } | null | undefined)?.code) {
    case 'ITEM_NAME_REQUIRED':
      return 'Enter an item name first.';
    case 'VAT_RATE_NOT_SET':
      return "VAT rate is not set in Settings, so this item's cost cannot be worked out.";
    case 'FX_RATE_NOT_SET':
      return 'Set the TT$/US$ rate in Settings first.';
    // D157.1: a line's Refresh asked for the row a person matched it to.
    case 'CATALOGUE_ROW_GONE':
      return 'The catalogue item this line is matched to is no longer in Master Catalogue, so the line is left as it was. Use Search Master Catalogue (the magnifier) to pick another item.';
    case 'CATALOGUE_ROW_UNPRICED':
      return 'The catalogue item this line is matched to has no price for this line, so the line is left as it was. Fix its price or unit in Master Catalogue, or use Search Master Catalogue (the magnifier) to pick another item.';
    default:
      return genericSentence;
  }
}

/**
 * Fetch one of the quotation files the server builds and hand it to the browser as a download.
 *
 * The JWT is only ever sent as a header (no cookie), so a bare link to the URL is always 401: fetch
 * with the header, and let a refusal throw through apiClient's one error path (`handleResponse`: the
 * `status`, `code` and `reason` intact, session expiry handled) - the house way for a blob download,
 * as `fetchClientInvoicePdf` does it.
 *
 * It used to save whatever came back, so a 404 or a 500 - a JSON error body - was downloaded as the
 * "PDF"; and it never returned its fetch, so the screens' `catch` could not run either. Four exports
 * carried a copy of this each; they differ only in the path.
 */
async function downloadQuotationFile(path: string, discountPercentage: number): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/quotations/${path}?discount=${discountPercentage}`, {
    method: 'GET',
    headers: getAuthHeader(),
  });
  if (!res.ok) await handleResponse(res);
  const blob = await res.blob();

  // Hand the blob to the browser through a temporary link
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.setAttribute('download', '');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
}

/**
 * What a failed quotation download says, chosen by the envelope's machine-readable `code` - never by
 * the server's prose, which a copy edit would silently break (CLAUDE.md invariant 6). `what` names the
 * file ("PDF", "Excel", "Client PDF", "Client Excel"). An unrecognised or absent code reads the
 * generic sentence.
 */
export function quotationDownloadErrorMessage(err: unknown, what: string): string {
  const code = err && typeof err === 'object' ? (err as { code?: unknown }).code : undefined;
  switch (code) {
    case 'QUOTATION_NOT_FOUND':
      return 'This quotation could not be found. Reload the page.';
    case 'PERMISSION_DENIED':
      return 'You do not have permission to download quotations.';
    default:
      return `Failed to download ${what}. Please try again.`;
  }
}

// Download quotation as PDF
export function downloadQuotationPDF(id: string, discountPercentage: number = 0): Promise<void> {
  return downloadQuotationFile(`${id}/download/pdf`, discountPercentage);
}

// Download quotation as Excel. There is one Excel — the fixed United Ship
// Chandlers layout the server builds (owner, 2026-09-29) — so there is no
// layout to name here.
export function downloadQuotationExcel(id: string, discountPercentage: number = 0): Promise<void> {
  return downloadQuotationFile(`${id}/download/excel`, discountPercentage);
}

// Download quotation as PDF (Client View - Limited Columns)
export function downloadQuotationClientPDF(id: string, discountPercentage: number = 0): Promise<void> {
  return downloadQuotationFile(`${id}/download/client-pdf`, discountPercentage);
}

// Download quotation as Excel (Client View). The server answers this route with
// the same single Excel as `downloadQuotationExcel`; the route is kept so old
// links still work.
export function downloadQuotationClientExcel(id: string, discountPercentage: number = 0): Promise<void> {
  return downloadQuotationFile(`${id}/download/client-excel`, discountPercentage);
}

// A LISTED-option supplier pick's own payload — shared by the real write
// (`updateQuotationItemVendor`) and its dry-run preview (owner decision C,
// 2026-09-24/25) below, so the two are always asked the identical question.
export interface SupplierPickPricingPayload {
  unitPrice?: number;
  currency?: string;
  uom?: string;
  source?: string;
  description?: string;
  /**
   * F1 (owner audit, 2026-09-25): what the lookup panel actually searched to
   * find this option — the panel searches freely (2026-09-23), so a pick can
   * name a product with nothing in common with the line's own name, and the
   * server resolves the option from THIS term rather than re-scoping around
   * the line's name, which refused a clean pick of an unrelated product
   * ("Cabbage White" line, "Lettuce Iceberg" pick — 409
   * SUPPLIER_OPTION_NOT_FOUND under the old scope). Omitted, the server falls
   * back to the line's own name — the ordinary same-name comparison.
   */
  searchTerm?: string;
}

// Update selected vendor for a quotation item.
//
// `pricing` is optional: pass it when the pick came from the live 3-source
// supplier dropdown (a price not necessarily pre-baked into the item's
// `vendorOptions`), so the vendor name AND its price/currency land in the
// SAME request. Two separate requests from one stale `quotation` snapshot is
// how a supplier pick used to revert itself — see QUO-00012.
//
// `description` names WHICH option was picked. The server resolves that option
// again, for the line's own unit, and prices the write from it — so the cost
// written is the one the row showed, and a price the line's unit cannot take is
// refused rather than applied (QUO-00068).
export const updateQuotationItemVendor = async (
  quotationId: string,
  itemIndex: number,
  vendorName: string,
  pricing?: SupplierPickPricingPayload
): Promise<{ success: boolean; data: QuotationDetailDto }> => {
  const token = localStorage.getItem('token');

  const response = await fetch(`${API_BASE_URL}/quotations/${quotationId}/items/${itemIndex}/vendor`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ vendorName, ...(pricing || {}) }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // The code and the refusal reason travel with the error, because they are
    // what the page words its message from (invariant 6); the server's sentence
    // stays on `message` for the console only.
    const error = new Error(errorData.message || 'Failed to update vendor') as Error & {
      code?: string;
      reason?: string;
    };
    error.code = typeof errorData.code === 'string' ? errorData.code : undefined;
    error.reason = typeof errorData.reason === 'string' ? errorData.reason : undefined;
    throw error;
  }

  return response.json();
};

// Preview what a LISTED supplier-option pick would write onto a quotation
// line, without saving — owner decision C (2026-09-24/25): the in-app confirm
// shown before a supplier pick applies must show the SAME before/after the
// catalogue pick already shows, and the "after" must be what the server will
// actually write. This calls a dry-run endpoint that runs the exact same
// resolution and pricing `updateQuotationItemVendor` uses
// (`quotationLineSupplierPrice.resolveListedSupplierPick`) and saves nothing —
// never a second, client-side computation of the price (QUO-00068 is what
// that guess already cost once).
//
// Same request/error shape as `updateQuotationItemVendor` on purpose: the two
// are asked through the identical `pricing` payload, and a caller maps either
// one's failure through the same `code`/`reason` (`supplierPickErrorMessage`).
//
// N2 (owner re-audit, 2026-09-25): rebuilt on `apiClient.postJson`, the one
// fetch wrapper (CLAUDE.md forbids a second one) — this used to hand-roll its
// own `fetch` + bearer header, which meant a 401 here never ran the
// TOKEN_EXPIRED session-expiry flow `apiClient`'s `handleResponse` owns.
// `postJson`'s thrown error already carries `status`/`code`/`reason`
// (`reason` added to `apiClient` in the same change, so this endpoint's
// refusal-shaped errors keep reaching `supplierPickErrorMessage` intact).
export const previewQuotationItemVendorPick = async (
  quotationId: string,
  itemIndex: number,
  vendorName: string,
  pricing?: SupplierPickPricingPayload
): Promise<{ success: boolean; data: QuotationItemDto }> => {
  return postJson<{ success: boolean; data: QuotationItemDto }>(
    `/quotations/${quotationId}/items/${itemIndex}/vendor/preview`,
    { vendorName, ...(pricing || {}) }
  );
};

// Send quotation via email
export const sendQuotation = async (
  quotationId: string,
  emailData: {
    rfqNumber: string;
    date: string;
    clientName: string;
    vesselName: string;
    subject: string;
    description: string;
    recipients?: string[];
  }
): Promise<{
  success: boolean;
  data: QuotationDetailDto;
  /** Addresses the mail server refused while taking the rest: the quotation went, but not to these. Absent when none. */
  rejectedRecipients?: string[];
}> =>
  // On `apiClient.postJson`, the one fetch wrapper: a refusal throws an error carrying the
  // envelope's `code`, `reason` and `errors` (as `validationErrors` / `details`), which is what
  // `sendQuotationErrorMessage` words the screen from. This used to hand-roll its own fetch and
  // throw `new Error(message)`, so the code was lost and a 401 never ran the session-expiry flow.
  postJson<{ success: boolean; data: QuotationDetailDto; rejectedRecipients?: string[] }>(`/quotations/${quotationId}/send`, emailData);

/**
 * What a refused send says when lines stand in its way (D105.2, D105.7: a line with no, US$0.00 or a
 * negative selling price cannot be sent).
 * One spelling for both places that say it - the page's check before the send form opens and the
 * server's own answer (QUOTATION_NOT_SENDABLE) - so they can never word it differently.
 * `lineMessages` are the confirmation gate's per-line sentences ("Line 3 (Rope) has no selling price.").
 */
export function notSendableMessage(lineMessages: string[]): string {
  return lineMessages.length > 0
    ? `This quotation cannot be sent until these are fixed:\n${lineMessages.map((line) => `• ${line}`).join('\n')}\nPrice those lines or delete them. Nothing was sent.`
    : 'This quotation cannot be sent while a line has a missing or negative selling price. Price it or delete the line. Nothing was sent.';
}

/**
 * What a send says when the mail server took the mail for some recipients and refused others: the
 * quotation WENT, so this is not a failure, but the clerk is told who did not get it.
 */
export function rejectedRecipientsMessage(addresses: string[]): string {
  const list = addresses.join(', ');
  return `The quotation was sent, but the email server rejected ${list}. ${addresses.length === 1 ? 'That address' : 'Those addresses'} did not get it.`;
}

/**
 * Copy for a failed send, chosen by the envelope's machine-readable `code` (and `reason`) — never
 * by the server's prose, which a copy edit would silently break (CLAUDE.md invariant 6).
 *
 * It lives beside `sendQuotation` because this is that endpoint's contract: the page gets one
 * spelling of each. "Nothing was sent" is said wherever it is true, and the one failure where the
 * email DID go (the quotation could not be saved as Sent) says so, because the clerk would
 * otherwise send it a second time. An unrecognised or absent code reads the old generic sentence.
 */
export function sendQuotationErrorMessage(err: unknown): string {
  const e = (err && typeof err === 'object' ? err : {}) as {
    code?: unknown;
    reason?: unknown;
    details?: unknown;
    validationErrors?: unknown;
  };
  const details = (e.details && typeof e.details === 'object' ? e.details : {}) as Record<string, unknown>;
  const texts = (list: unknown, pick: (entry: any) => unknown): string[] =>
    (Array.isArray(list) ? list : [])
      .map((entry) => pick(entry))
      .filter((text): text is string => typeof text === 'string' && text.trim() !== '');
  const bullets = (lines: string[]) => lines.map((line) => `• ${line}`).join('\n');
  const lineLabel = (entry: any) =>
    entry && entry.lineNumber != null
      ? `Line ${entry.lineNumber}${entry.itemName ? ` (${entry.itemName})` : ''}`
      : null;

  switch (e.code) {
    case 'QUOTATION_SEND_SUBJECT_REQUIRED':
      return 'Enter a subject, then send again. Nothing was sent.';
    case 'QUOTATION_SEND_NO_RECIPIENT':
      return 'Add at least one recipient email address, then send again. Nothing was sent.';
    case 'QUOTATION_SEND_INVALID_RECIPIENT': {
      const invalid = texts(details.invalid, (address) => address);
      return invalid.length > 0
        ? `${invalid.join(', ')} ${invalid.length === 1 ? 'is not a valid email address' : 'are not valid email addresses'}. ` +
            `Correct ${invalid.length === 1 ? 'it' : 'them'}, then send again. Nothing was sent.`
        : 'A recipient email address is not valid. Correct it, then send again. Nothing was sent.';
    }
    case 'QUOTATION_NOT_FOUND':
      return 'This quotation could not be found. Reload the page. Nothing was sent.';
    case 'PERMISSION_DENIED':
      return 'You do not have permission to send quotations. Nothing was sent.';
    // The send gate (D105.2): one entry per line, the same entries the confirmation gate lists.
    case 'QUOTATION_NOT_SENDABLE':
      return notSendableMessage(texts(e.validationErrors, (blocker) => blocker && blocker.message));
    case 'QUOTATION_HAS_UNPRICED_LINES': {
      const lines = texts(details.unpricedLines, lineLabel);
      return lines.length > 0
        ? `This quotation cannot be sent: these lines have no cost yet.\n${bullets(lines)}\nPrice them first. Nothing was sent.`
        : 'This quotation cannot be sent while a line has no cost. Price it first. Nothing was sent.';
    }
    case 'QUOTATION_HAS_AMOUNTS_NOT_THE_ASK': {
      const lines = texts(details.amountLines, (line) => line && line.message);
      return lines.length > 0
        ? `This quotation cannot be sent: these lines do not state the amount the vessel asked for.\n${bullets(lines)}\nCorrect the quantity, or mark the amount as right on the line. Nothing was sent.`
        : 'This quotation cannot be sent while a line does not state the amount the vessel asked for. Nothing was sent.';
    }
    case 'EMAIL_NOT_CONFIGURED':
      return 'Email is not set up on this server, so nothing was sent. Ask an administrator to set up the mail account.';
    case 'EMAIL_SEND_FAILED':
      switch (e.reason) {
        case 'SMTP_UNREACHABLE':
          return 'The email server could not be reached, so nothing was sent. Try again in a minute.';
        case 'SMTP_AUTH_FAILED':
          return 'The email server refused our login, so nothing was sent. Ask an administrator to check the mail account password.';
        case 'RECIPIENT_REJECTED': {
          const rejected = texts(details.rejected, (address) => address);
          return rejected.length > 0
            ? `The email server rejected ${rejected.join(', ')}, so nothing was sent. Check ${rejected.length === 1 ? 'that address' : 'those addresses'} and send again.`
            : 'The email server rejected a recipient address, so nothing was sent. Check the addresses and send again.';
        }
        default:
          return 'The email could not be sent, so nothing was sent or saved. Please try again.';
      }
    case 'QUOTATION_SHEET_TOTAL_MISMATCH':
      return "The Excel sheet does not add up to the quotation's final price, so nothing was sent. Contact support.";
    case 'QUOTATION_EMAILED_NOT_RECORDED':
      return 'The quotation WAS emailed to the client, but it could not be saved as Sent. Do not send it again — reload the page and check its status.';
    default:
      return 'Failed to send quotation. Please try again.';
  }
}
